import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  useServerTime,
  getCountdownState,
  formatCountdownUnits,
  CountdownState,
} from '@/lib/countdown-utils';

interface CountdownTimerProps {
  targetDate: string;
  mode: 'hero' | 'compact' | 'inline';
  label?: string;
  onStateChange?: (state: CountdownState) => void;
}

const HEBREW_UNITS = {
  days: 'ימים',
  hours: 'שעות',
  minutes: 'דקות',
  seconds: 'שניות',
} as const;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

export function CountdownTimer({ targetDate, mode, label, onStateChange }: CountdownTimerProps) {
  const { now: serverNow, ready } = useServerTime();
  const [msRemaining, setMsRemaining] = useState<number>(() =>
    Math.max(0, new Date(targetDate).getTime() - Date.now())
  );
  const stateRef = useRef<CountdownState | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Notify parent on state transition
  useEffect(() => {
    if (!ready) return;
    const current = getCountdownState(serverNow());
    if (current !== stateRef.current) {
      stateRef.current = current;
      onStateChange?.(current);
    }
  }, [ready, serverNow, onStateChange]);

  // Tick logic: 1s when <24h remaining, 60s otherwise
  useEffect(() => {
    if (!ready) return;

    function tick() {
      const ms = Math.max(0, new Date(targetDate).getTime() - serverNow());
      setMsRemaining(ms);

      // Check if we need to switch interval (crossing 24h threshold)
      const threshold24h = 24 * 60 * 60 * 1000;
      const shouldBeSecondly = ms < threshold24h;
      const currentTickMs = shouldBeSecondly ? 1000 : 60_000;

      // Notify parent of state change
      const newState = getCountdownState(serverNow());
      if (newState !== stateRef.current) {
        stateRef.current = newState;
        onStateChange?.(newState);
      }

      if (ms === 0) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        return;
      }

      return currentTickMs;
    }

    // Initial tick immediately, then set interval
    tick();
    const initialMs = Math.max(0, new Date(targetDate).getTime() - serverNow());
    const threshold24h = 24 * 60 * 60 * 1000;
    const tickMs = initialMs < threshold24h ? 1000 : 1000;

    intervalRef.current = setInterval(tick, tickMs);

    // Re-setup when crossing 24h threshold: watch every second if near boundary
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [ready, targetDate, serverNow, onStateChange]);

  // Re-mount interval when crossing 24h boundary
  const [, forceRender] = useState(0);
  useEffect(() => {
    if (!ready) return;
    const threshold24h = 24 * 60 * 60 * 1000;
    if (msRemaining <= threshold24h && msRemaining > threshold24h - 2000) {
      // Just crossed the 24h boundary — rebuild interval
      forceRender(n => n + 1);
    }
  }, [msRemaining, ready]);

  // Loading skeleton
  if (!ready) {
    if (mode === 'inline') {
      return <span className="font-mono animate-pulse text-muted-foreground">--:--:--</span>;
    }
    if (mode === 'compact') {
      return (
        <span className="text-sm font-mono animate-pulse text-muted-foreground">
          {label ? `${label} ` : ''}--:--:--
        </span>
      );
    }
    // hero skeleton
    return (
      <div className="flex flex-col items-center gap-3" dir="rtl">
        {label && (
          <p className="text-sm text-muted-foreground uppercase tracking-[0.2em]">{label}</p>
        )}
        <div className="flex flex-row-reverse gap-2 sm:gap-3">
          {(['ימים', 'שעות', 'דקות', 'שניות'] as const).map((unit) => (
            <div
              key={unit}
              className="bg-[hsl(0,0%,7%)] rounded-xl border border-[hsl(0,0%,14%)] px-3 py-3 sm:px-5 sm:py-4 min-w-[64px] sm:min-w-[80px] flex flex-col items-center gap-1 animate-pulse"
            >
              <div className="text-3xl sm:text-4xl font-extrabold font-mono tracking-tight leading-none text-muted-foreground">
                --
              </div>
              <div className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-[0.15em]">{unit}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Countdown completed
  if (msRemaining === 0) {
    if (mode === 'inline') {
      return <span className="font-mono text-orange-500">00:00:00</span>;
    }
    if (mode === 'compact') {
      return (
        <span className="text-sm font-mono text-orange-400">
          {label ? `${label} ` : ''}הזמן נגמר
        </span>
      );
    }
    // hero completed
    return (
      <div className="flex flex-col items-center gap-3" dir="rtl">
        {label && (
          <p className="text-sm text-muted-foreground uppercase tracking-[0.2em]">{label}</p>
        )}
        <div className="text-xl font-bold text-orange-500">הזמן נגמר</div>
      </div>
    );
  }

  const { days, hours, minutes, seconds } = formatCountdownUnits(msRemaining);
  const threshold48h = 48 * 60 * 60 * 1000;
  const isUrgent = msRemaining < threshold48h;
  const accentClass = isUrgent ? 'text-orange-500' : '';

  // ── Inline mode ──────────────────────────────────────────────────────────────
  if (mode === 'inline') {
    const threshold24h = 24 * 60 * 60 * 1000;
    const text =
      msRemaining < threshold24h
        ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
        : `${days} ימים ${pad(hours)} שעות`;
    return (
      <span className={`font-mono ${isUrgent ? 'text-orange-500' : ''}`}>{text}</span>
    );
  }

  // ── Compact mode ─────────────────────────────────────────────────────────────
  if (mode === 'compact') {
    const threshold24h = 24 * 60 * 60 * 1000;
    const text =
      msRemaining < threshold24h
        ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
        : `${days} ימים ${pad(hours)} שעות`;
    return (
      <span
        className={`text-sm font-mono ${isUrgent ? 'text-orange-400' : 'text-foreground'}`}
        dir="rtl"
      >
        {label ? `${label} ` : ''}
        {text}
      </span>
    );
  }

  // ── Hero mode ─────────────────────────────────────────────────────────────────
  const units = [
    { value: days, label: HEBREW_UNITS.days },
    { value: hours, label: HEBREW_UNITS.hours },
    { value: minutes, label: HEBREW_UNITS.minutes },
    { value: seconds, label: HEBREW_UNITS.seconds },
  ];

  return (
    <div className="flex flex-col items-center gap-3" dir="rtl">
      {label && (
        <p className="text-sm text-muted-foreground uppercase tracking-[0.2em]">{label}</p>
      )}
      <div className="flex flex-row-reverse gap-2 sm:gap-3">
        {units.map(({ value, label: unitLabel }) => (
          <motion.div
            key={unitLabel}
            layout
            className="bg-[hsl(0,0%,7%)] rounded-xl border border-[hsl(0,0%,14%)] px-3 py-3 sm:px-5 sm:py-4 min-w-[64px] sm:min-w-[80px] flex flex-col items-center gap-1"
          >
            <span
              className={`text-3xl sm:text-4xl font-extrabold font-mono tracking-tight leading-none ${
                isUrgent ? 'text-orange-500' : accentClass || 'text-foreground'
              }`}
            >
              {pad(value)}
            </span>
            <span className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-[0.15em]">
              {unitLabel}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default CountdownTimer;
